/**
 * 老师半身像展示台
 * ============================================================
 * 用途：在课程教学页**上半部分**展示老师的半身立绘。
 *
 * ── 与现有 VirtualTutor 的分工 ───────────────────────────────
 *   VirtualTutor（已有）  圆形小头像，用于紧凑场景（如列表、页脚）
 *   本组件（新增）        大尺寸半身像，占用页面上半部分作为主视觉
 *   两者共用同一套状态入参（state / level），都从 useLipSync 取值，
 *   **不重复实现音频与口型驱动逻辑**。
 *
 * ── 口型：两条路径，按素材有无自动切换 ──────────────────────
 *
 *   A. 有预生成片段（clipUrl 非空）
 *      播该片段视频。片段由 MuseTalk **按我们的 TTS 音频生成**，
 *      且 inference 收尾时把同一条音频封装进视频 ——
 *      所以片段的音轨就是 TTS 本身，**音画天然同步，不需要任何对齐**。
 *
 *   B. 没有片段（动态文本 / 尚未生成 / 播放被拦截）
 *      仍**不做假口型**。在静态照片上叠黑色椭圆比不叠更糟 ——
 *      真实人脸的嘴部位置与开合形状无法用椭圆近似。
 *      此时只显示呼吸光环 + 波形表示"正在说话"。
 *
 *   两条路径对用户是连续的：没有片段时不黑屏、不报错，安静回到立绘。
 *
 * ⚠️ 视频**不能静音**。它的音轨就是老师要说的话 ——
 *    调用方必须保证走片段路径时不要同时再播一遍 TTS，否则两条音轨重叠。
 *
 * ── 素材缺失时的降级 ────────────────────────────────────────
 *   portrait 为 null 或加载失败 → 显示优雅占位（渐变 + 图标 + 名字），
 *   **不是破图**，也不影响上课。图片加载中显示骨架屏避免布局跳动。
 */

import { useState, useEffect, useRef } from 'react';
import { markClipMissingByUrl } from '../lib/tutorClip';

/** 状态 → 文案与配色 */
const STATE_META = {
  idle: { label: '待机', dot: 'bg-zinc-300' },
  speaking: { label: '示范中', dot: 'bg-emerald-400' },
  listening: { label: '在听你说', dot: 'bg-rose-400' },
  thinking: { label: '思考中', dot: 'bg-amber-400' },
};

export default function TeacherPortraitStage({
  tutor,
  asset,
  state = 'idle',
  level = 0,
  clipUrl = null,
  onClipEnded,
  className = '',
}) {
  const [imgState, setImgState] = useState('loading'); // loading | ok | error
  /**
   * 进入了「静音降级」：浏览器拦下了带声自动播放，改为静音播放保住画面。
   * 此时会叠一个显眼的「点这里开声音」按钮（见下方渲染）。
   */
  const [mutedFallback, setMutedFallback] = useState(false);
  const videoRef = useRef(null);

  // 换老师/换素材时要重置加载状态，否则会一直停在上一张的结果
  useEffect(() => {
    setImgState('loading');
  }, [asset?.portrait]);

  /*
   * 片段切换时从头播。
   *
   * 用 effect 手动 play() 而不是 <video autoPlay>：
   * 连续听同一句（重复点「老师」）时 src 没变，autoPlay 不会重新触发，
   * 视频会卡在结束帧。归零 currentTime 再 play 才符合「再点一次再听一遍」的直觉。
   *
   * ── 三级播放策略（重要）──────────────────────────────────────
   *   1. 带声播放 —— 正常路径。
   *   2. 被自动播放策略拒绝 → **静音播放**，保证画面（口型、头部动作）动起来，
   *      并叠一个「点这里开声音」按钮。
   *   3. 静音也失败 → 如实上报，父组件走它自己的兜底。
   *
   * ⚠️ 为什么第 2 级不能省：
   *    浏览器对**带音轨**的媒体有手势要求。而「进场自动播开场讲解」恰好
   *    经常没有手势 —— 从课程列表点进来算手势，但**刷新页面后直接进课程页、
   *    或从收藏夹打开**都不算。这时带声 play() 会被 NotAllowedError 拒绝。
   *
   *    历史表现：讲解段（普通话）一帧都不动，而点了「开始上课」之后的示范
   *    （粤语）正常 —— 因为后者带用户手势。从界面上看，前者和「功能没做」
   *    完全一样，排查时极易误判成后端或素材问题。
   *
   *    静音媒体不受任何自动播放限制，所以静音降级是「无论如何先让画面动起来」
   *    的可靠手段；声音则用一个可点的按钮补回来（点击即产生手势）。
   */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !clipUrl) return;

    setMutedFallback(false);

    try {
      v.currentTime = 0;
    } catch {
      /* 元数据未就绪时设 currentTime 会抛，忽略；play() 会自动从头 */
    }

    v.muted = false;
    v.play().catch((err) => {
      const blocked = err?.name === 'NotAllowedError';

      if (!blocked) {
        /*
         * 多数是 NotSupportedError —— 源加载失败（文件不存在 / 格式不支持）。
         * 那件事 onError 已经处理过、也记了负缓存，这里**不再重复告警**：
         * 一次失败刷两条日志，用户会以为坏得很严重。
         */
        onClipEnded?.({ ok: false, reason: 'play-error' });
        return;
      }

      console.warn('[老师片段] 带声自动播放被浏览器拦下，改用静音播放:', err?.name);

      // 降级：静音播放，先让「虚拟人在动」这件事成立
      v.muted = true;
      v.play()
        .then(() => setMutedFallback(true))
        .catch((e2) => {
          console.warn('[老师片段] 静音播放也失败:', e2?.name, e2?.message);
          onClipEnded?.({ ok: false, reason: 'blocked' });
        });
    });
  }, [clipUrl, onClipEnded]);

  const meta = STATE_META[state] || STATE_META.idle;
  const isSpeaking = state === 'speaking';
  const isListening = state === 'listening';
  const hasPortrait = !!asset?.portrait && imgState !== 'error';

  return (
    <section
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-b from-teal-50 to-sky-50 shadow-sm dark:from-teal-950/40 dark:to-sky-950/40 ${className}`}
      aria-label="虚拟老师"
    >
      {/*
        立绘区。
        高度用视口比例 + 上下限，宽度加 max-w：
        教学页没有全局宽度容器，桌面浏览器下纯 4:3 会把图撑到上千像素高。
        这样手机上仍是竖屏卡片，桌面上是居中的手机宽度卡片。
      */}
      <div className="relative mx-auto h-[36vh] max-h-[340px] min-h-[220px] w-full max-w-md">
        {/* 说话时的呼吸光环（放在图后面，不影响面部） */}
        {isSpeaking && (
          <span
            aria-hidden="true"
            className="tutor-breathe absolute inset-x-8 bottom-0 top-6 rounded-[40%] bg-emerald-300/25 blur-2xl dark:bg-emerald-500/15"
          />
        )}

        {/* 骨架屏：避免素材加载期间布局跳动（片段路径用 poster 兜底，不需要它） */}
        {!clipUrl && imgState === 'loading' && hasPortrait && (
          <div className="absolute inset-0 animate-pulse bg-zinc-200/60 dark:bg-zinc-800/60" />
        )}

        {clipUrl ? (
          /*
           * 预生成口型片段。
           * poster 指向立绘 —— 视频元数据到达前显示立绘而不是黑屏，
           * 用户不会看到"画面闪黑一下"。
           *
           * muted 只在**静音降级**时为 true（那条路走完仍然会用按钮把声音补回来）。
           * 正常路径不静音：片段的音轨就是老师要说的话。
           */
          <>
            <video
              ref={videoRef}
              src={clipUrl}
              muted={mutedFallback}
              poster={asset?.portrait || undefined}
              className="relative h-full w-full object-cover object-top"
              playsInline
              preload="auto"
              aria-label={tutor?.name ? `${tutor.name} 老师的口型示范` : '老师口型示范'}
              onEnded={() => onClipEnded?.({ ok: true })}
              onError={() => {
                /*
                 * 片段不存在（尚未生成 / 这一课没覆盖到）。
                 *
                 * 记入负缓存，本会话不再重试该句。
                 * 日志用 info 级：走乐观解析时这是**预期路径**，
                 * 用 warn 会在控制台刷成一片红，看起来像功能坏了。
                 */
                markClipMissingByUrl(clipUrl);
                console.info('[老师片段] 该句暂无口型片段，回退语音朗读:', clipUrl);
                onClipEnded?.({ ok: false, reason: 'error' });
              }}
            />

            {/*
             * 静音降级提示。
             *
             * 浏览器拦下了带声自动播放，我们已经改成静音播放把画面救回来了，
             * 但**声音才是教学内容**（这是语言课）。所以必须给一个足够显眼的
             * 入口把声音补回来 —— 用户点一下即产生手势，从头带声重播。
             *
             * 放在画面下沿居中：不遮挡脸（脸在中上部），又是视线自然落点。
             */}
            {mutedFallback && (
              <button
                type="button"
                onClick={() => {
                  const v = videoRef.current;
                  setMutedFallback(false);
                  if (!v) return;
                  v.muted = false;
                  try {
                    v.currentTime = 0; // 从头带声重播，避免声音从半截进来
                  } catch {
                    /* 忽略 */
                  }
                  v.play().catch(() => {});
                }}
                className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-black/70 px-3.5 py-2 text-[11px] font-bold text-white shadow-lg backdrop-blur active:scale-95"
              >
                <span className="material-icons !text-sm">volume_off</span>
                点这里开声音
              </button>
            )}
          </>
        ) : hasPortrait ? (
          /*
           * 立绘只带**极轻微的呼吸位移**（tutor-breathe：3.6s，上下 3px）。
           *
           * ⚠️ 这里刻意**不做**「讲话时的整图晃动」。
           *    曾经加过 tutor-talking（位移 ±9px + 旋转 ±1.1°），
           *    想做「没有口型片段时也让画面动起来」。用户实测后否掉了：
           *      「企图加大整个图片晃动来提高动态效果，但虚拟人没有任何动作，
           *        这种做法是不对的。」
           *    这个判断成立 —— 整图位移让**图片**在动，不等于让**人物**在动，
           *    看起来只是画面在抖，属于用视觉噪声冒充内容。
           *    真正的说话动作只能来自口型片段本身。
           *
           * 保留呼吸的作用仅止于"画面不是一张死图"，不承担"假装在说话"。
           * 视频路径同样不加 —— 片段自带头部动作与口型，叠加会发飘。
           * 呼吸动效尊重 prefers-reduced-motion（见 index.css）。
           */
          <img
            src={asset.portrait}
            alt={tutor?.name ? `${tutor.name} 老师的形象` : '虚拟老师'}
            className={`tutor-breathe relative h-full w-full object-cover object-top transition-opacity duration-500 ${
              imgState === 'ok' ? 'opacity-100' : 'opacity-0'
            }`}
            draggable={false}
            onLoad={() => setImgState('ok')}
            onError={() => setImgState('error')}
          />
        ) : (
          <Placeholder name={tutor?.name} />
        )}

        {/*
          口型区域 —— 请勿把它当作口型同步。

          这是**音量驱动的开合示意**：
            · 位置来自 manifest 的 mouthPosition（人工标定，不是识别出来的）
            · 幅度来自 useLipSync 读到的真实音频音量
          它让老师"看起来在说话"，但**不区分音素**，嘴形与实际发音无关。
          界面上有明确标注；接入数字人后端后应整体替换掉。
        */}
        {hasPortrait && asset?.lipSync === 'amplitude' && asset?.mouthPosition && isSpeaking && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute rounded-[50%] bg-zinc-900/45"
            style={{
              left: `${asset.mouthPosition.x}%`,
              top: `${asset.mouthPosition.y}%`,
              width: `${asset.mouthPosition.w}%`,
              height: `${asset.mouthPosition.h}%`,
              transform: `translate(-50%, -50%) scaleY(${(0.45 + level * 1.5).toFixed(2)}) scaleX(${(0.9 + level * 0.25).toFixed(2)})`,
              transformOrigin: 'center center',
              filter: 'blur(3px)',
              transition: 'transform 70ms linear',
            }}
          />
        )}

        {/* 左上角：老师名字与状态 */}
        <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 shadow-sm backdrop-blur-sm dark:bg-zinc-900/70">
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} ${isSpeaking ? 'animate-pulse' : ''}`} />
          <span className="text-[11px] font-bold text-zinc-700 dark:text-zinc-200">
            {tutor?.name || '老师'}
          </span>
          <span className="text-[10px] text-zinc-400">{meta.label}</span>
        </div>

        {/*
          右上角：如实标注素材性质。
          AI 生成的图像不得被当成真人老师照片 —— 需求要求"说明素材来源"。
        */}
        {/* 口型能力如实标注：不接数字人就明说，避免用户以为是真口型同步 */}
        {hasPortrait && asset?.lipSync === 'amplitude' && (
          <span className="absolute bottom-11 right-3 rounded-full bg-white/75 px-2 py-0.5 text-[9px] text-zinc-500 backdrop-blur-sm dark:bg-zinc-900/70 dark:text-zinc-400">
            口型为音量示意
          </span>
        )}

        {asset?.credit?.type === 'ai-generated' && (
          <span className="absolute right-3 top-3 rounded-full bg-white/75 px-2 py-1 text-[9px] text-zinc-500 backdrop-blur-sm dark:bg-zinc-900/70 dark:text-zinc-400">
            AI 生成形象
          </span>
        )}

        {/*
          底部波形：表示"老师正在说话 / 正在听"。

          ⚠️ 这是**装饰动画，不是音频数据**。
             项目已关闭 Web Audio 分析（见 useLipSync 的 analysis 选项），
             所以这里拿不到真实音量。用 CSS 动画表示状态，
             比一排静止的小点诚实 —— 但也不要误以为它反映音量。
        */}
        {(isSpeaking || isListening) && (
          <div className="absolute inset-x-0 bottom-0 flex h-10 items-end justify-center gap-[3px] bg-gradient-to-t from-white/85 to-transparent px-6 pb-2 dark:from-zinc-900/85">
            {Array.from({ length: 22 }).map((_, i) => {
              // 中心高、两侧低的钟形包络，让波形有起伏而不是一条直线
              const k = 1 - Math.abs(i - 10.5) / 12;
              // 相邻柱子错开延迟，视觉上有流动感
              const delay = (i % 5) * 90;
              return (
                <span
                  key={i}
                  className={`tutor-wave-bar w-[3px] rounded-full ${
                    isListening ? 'bg-rose-400/80' : 'bg-emerald-400/80'
                  }`}
                  style={{
                    animationDelay: `${delay}ms`,
                    // 用 CSS 变量传包络高度，避免每条内联动画
                    '--wave-max': `${Math.max(4, k * 22)}px`,
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* 图片加载失败：明确告知但不打断上课 */}
      {imgState === 'error' && (
        <p className="absolute inset-x-0 bottom-0 bg-amber-100/90 px-3 py-1.5 text-center text-[10px] text-amber-800 dark:bg-amber-900/70 dark:text-amber-200">
          老师立绘加载失败，已使用占位图。上课不受影响。
        </p>
      )}
    </section>
  );
}

/**
 * 素材缺失时的优雅占位
 *
 * 刻意**不画一张假脸** —— 用渐变 + 图标表示"形象待配置"，
 * 比一张粗糙的占位人像更诚实，也不会让用户以为那就是老师。
 */
function Placeholder({ name }) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center bg-gradient-to-b from-teal-100/70 to-sky-100/70 dark:from-teal-950/50 dark:to-sky-950/50">
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/70 shadow-sm dark:bg-zinc-800/70">
        <span className="material-icons text-5xl text-teal-500/70">record_voice_over</span>
      </div>
      <p className="mt-3 text-sm font-bold text-zinc-600 dark:text-zinc-300">{name || '老师'}</p>
      <p className="mt-0.5 text-[10px] text-zinc-400">形象待配置</p>
    </div>
  );
}
